import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, ClipboardCheck, Save, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { vetassess } from '@/lib/api';

const STAGE_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700', DOCUMENTS_COLLECTED: 'bg-blue-100 text-blue-700',
  FEE_PAID: 'bg-indigo-100 text-indigo-700', SUBMITTED: 'bg-amber-100 text-amber-700',
  UNDER_ASSESSMENT: 'bg-purple-100 text-purple-700', AWAITING_INFO: 'bg-orange-100 text-orange-700',
  ASSESSED_POSITIVE: 'bg-emerald-100 text-emerald-700', ASSESSED_NEGATIVE: 'bg-rose-100 text-rose-700',
  WITHDRAWN: 'bg-slate-100 text-slate-500',
};

export default function VetassessDetailPage() {
  const { id } = useParams();
  const [sub, setSub] = React.useState(null);
  const [stages, setStages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [refNo, setRefNo] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([vetassess.get(id), vetassess.stages()])
      .then(([s, st]) => { setSub(s); setStages(st.stages); setRefNo(s.reference_number || ''); setNotes(s.notes || ''); })
      .finally(() => setLoading(false));
  }, [id]);
  React.useEffect(load, [load]);

  const toggle = async (idx) => {
    const r = await vetassess.toggle(id, idx);
    setSub({ ...sub, checklist: r.checklist });
  };
  const changeStage = async (v) => {
    setSaving(true);
    try { const r = await vetassess.update(id, { stage: v }); setSub(r); } finally { setSaving(false); }
  };
  const saveMeta = async () => {
    setSaving(true);
    try { const r = await vetassess.update(id, { reference_number: refNo, notes: notes }); setSub(r); } finally { setSaving(false); }
  };

  if (loading || !sub) return <div className="flex h-full items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;

  const progress = Math.round(sub.checklist.filter((c) => c.done).length / sub.checklist.length * 100);

  return (
    <div className="p-6">
      <Link to="/app/vetassess" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1A3A5C]">
        <ArrowLeft className="h-4 w-4" /> All VETASSESS submissions
      </Link>

      <div className="mt-4 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-6 w-6 text-[#2ECC9A]" />
            <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">{sub.applicant_name}</h1>
            <Badge className={(STAGE_COLORS[sub.stage] || 'bg-slate-100') + ' text-[10px] uppercase tracking-wider'}>{sub.stage.replace(/_/g,' ')}</Badge>
          </div>
          <div className="mt-1 text-sm text-slate-500">{sub.occupation} · ANZSCO {sub.anzsco_code} · {sub.application_type} · Matter {sub.matter_ref}</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sub.stage} onValueChange={changeStage} disabled={saving}>
            <SelectTrigger className="h-10 w-52"><SelectValue /></SelectTrigger>
            <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-[#1A3A5C]">Document checklist</div>
                <div className="text-xs font-medium text-[#2ECC9A]">{progress}% complete</div>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-[#2ECC9A] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-4 space-y-2">
                {sub.checklist.map((c, idx) => (
                  <button key={idx} onClick={() => toggle(idx)}
                    className="flex w-full items-center gap-3 rounded-md border border-slate-100 bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100">
                    <Checkbox checked={c.done} className="data-[state=checked]:bg-[#2ECC9A] data-[state=checked]:border-[#2ECC9A]" />
                    <span className={`text-sm ${c.done ? 'text-slate-500 line-through' : 'text-[#1A3A5C]'}`}>{c.label}</span>
                    {c.done && <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-600" />}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 border-slate-200">
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-[#1A3A5C]">Stage history</div>
              <div className="mt-4 space-y-3">
                {(sub.events || []).slice().reverse().map((ev, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#2ECC9A]" />
                    <div>
                      <div className="text-xs text-slate-500">{new Date(ev.at).toLocaleString()}</div>
                      <div className="text-sm font-medium text-[#1A3A5C]">{ev.stage.replace(/_/g,' ')}</div>
                      {ev.note && <div className="text-xs text-slate-600">{ev.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500">Expected outcome</div>
              <div className="mt-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#2ECC9A]" />
                <div className="font-serif text-xl font-bold text-[#1A3A5C]">{sub.expected_outcome_by}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">Based on VETASSESS median processing time.</div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-[#1A3A5C]">Submission details</div>
              <div className="mt-3 space-y-3">
                <div><Label className="text-xs">VETASSESS reference</Label><Input value={refNo} onChange={(e)=>setRefNo(e.target.value)} placeholder="VET-2025-…" className="mt-1" /></div>
                <div><Label className="text-xs">Internal notes</Label><Textarea rows={4} value={notes} onChange={(e)=>setNotes(e.target.value)} className="mt-1" /></div>
                <Button onClick={saveMeta} disabled={saving} className="w-full gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
